import { useCallback, useMemo } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import type { FilterEntityType } from "@/lib/filters/types";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

type CampaignFilterObject = Extract<
  FilterEntityType,
  "account" | "opportunity" | "touchpoint"
>;

interface CampaignFiltersState {
  account: FilterGroup;
  opportunity: FilterGroup;
  touchpoint: FilterGroup;
}

const defaultState: CampaignFiltersState = {
  account: EMPTY_GROUP,
  opportunity: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
};

/**
 * Hook to manage campaign filter state for Account, Opportunity & Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Exposes convenience helpers for serialising the filters into the single
 *   `filters` query-parameter understood by the backend.
 */
export function useCampaignFilterState(scope: string) {
  // Use tracked sessionStorage for automatic change detection
  // Migrated from filters::v1 to metrics::filters::v1 for clarity
  const storageKey = `${scope}::metrics::filters::${STORAGE_VERSION}`;
  const [state, setState] = useTrackedSessionStorage<CampaignFiltersState>(
    storageKey,
    defaultState,
    {
      // Migrate from old location
      migration: {
        from: {
          key: `${scope}::filters::${STORAGE_VERSION}`,
          storage: "sessionStorage",
          organizationScoped: true,
        },
        cleanup: true, // Remove the old key after migration
      },
    },
  );

  // ---------------------------------------------------------------------------
  // Update helpers
  // ---------------------------------------------------------------------------

  const setAccountFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: filters,
        opportunity: prev?.opportunity || EMPTY_GROUP,
        touchpoint: prev?.touchpoint || EMPTY_GROUP,
      })),
    [setState],
  );

  const setOpportunityFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: prev?.account || EMPTY_GROUP,
        opportunity: filters,
        touchpoint: prev?.touchpoint || EMPTY_GROUP,
      })),
    [setState],
  );

  const setTouchpointFilters = useCallback(
    (filters: FilterGroup) =>
      setState((prev) => ({
        account: prev?.account || EMPTY_GROUP,
        opportunity: prev?.opportunity || EMPTY_GROUP,
        touchpoint: filters,
      })),
    [setState],
  );

  const clearAccountFilters = useCallback(() => {
    setState((prev) => ({
      account: EMPTY_GROUP,
      opportunity: prev?.opportunity || EMPTY_GROUP,
      touchpoint: prev?.touchpoint || EMPTY_GROUP,
    }));
  }, [setState]);

  const clearOpportunityFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account || EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: prev?.touchpoint || EMPTY_GROUP,
    }));
  }, [setState]);

  const clearTouchpointFilters = useCallback(() => {
    setState((prev) => ({
      account: prev?.account || EMPTY_GROUP,
      opportunity: prev?.opportunity || EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    }));
  }, [setState]);

  const clearAllFilters = useCallback(() => {
    setState({
      account: EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    });
  }, [setState]);

  // ---------------------------------------------------------------------------
  // Serialisation helpers – returns undefined when the group is effectively empty
  // ---------------------------------------------------------------------------

  const serialize = useCallback(
    (obj: CampaignFilterObject): string | undefined => {
      let group: FilterGroup;
      switch (obj) {
        case "account":
          group = state.account;
          break;
        case "opportunity":
          group = state.opportunity;
          break;
        case "touchpoint":
          group = state.touchpoint;
          break;
        default:
          throw new Error(`Unknown filter object: ${obj}`);
      }
      // Empty group results in `undefined` so we omit the query param entirely
      if (!group || group.filters.length === 0) return undefined;
      return JSON.stringify(group);
    },
    [state],
  );

  // Quick flags for UI badges etc.
  const hasAccountFilters = useMemo(
    () => state?.account?.filters?.length > 0,
    [state.account],
  );
  const hasOpportunityFilters = useMemo(
    () => state?.opportunity?.filters?.length > 0,
    [state.opportunity],
  );
  const hasTouchpointFilters = useMemo(
    () => state?.touchpoint?.filters?.length > 0,
    [state.touchpoint],
  );

  return {
    filterState: state,
    setFilterState: setState,
    accountFilters: state?.account || EMPTY_GROUP,
    opportunityFilters: state?.opportunity || EMPTY_GROUP,
    touchpointFilters: state?.touchpoint || EMPTY_GROUP,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAccountFilters,
    clearOpportunityFilters,
    clearTouchpointFilters,
    clearAllFilters,
    serialize,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  };
}
